import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import type { Currency } from '@/types'

/**
 * Composable exposing the authenticated company's currency and a shared
 * amount formatter, so views stop hardcoding "৳"/"BDT" and duplicating
 * their own formatAmount() helpers.
 *
 * Usage:
 *   const { formatAmount, currencyCode, currencySymbol } = useCurrency()
 *   formatAmount(1234.5) // "$1,234.50" (or per-row currency via the second arg)
 */
export function useCurrency() {
  const authStore = useAuthStore()

  const currency = computed<Currency | undefined>(() => authStore.company?.currency)
  const currencyCode = computed(() => currency.value?.code || 'BDT')
  const currencySymbol = computed(() => currency.value?.symbol || '৳')

  function formatAmount(amount: number | string | null | undefined, override?: { code?: string; symbol?: string } | string): string {
    const value = Number(amount ?? 0)
    const formatted = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    if (typeof override === 'string') {
      return `${override} ${formatted}`
    }
    const symbol = override?.symbol ?? override?.code ?? currencySymbol.value
    return `${symbol} ${formatted}`
  }

  /** Catalogue price: whole numbers show without decimals ("¥ 2,912"). */
  function formatPrice(amount: number | string | null | undefined, override?: { code?: string; symbol?: string }): string {
    const value = Number(amount ?? 0)
    const formatted = value.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(value) ? 0 : 2, maximumFractionDigits: 2 })
    return `${override?.symbol ?? override?.code ?? currencySymbol.value} ${formatted}`
  }

  function formatAmountWithCode(amount: number | string | null | undefined, code?: string): string {
    const value = Number(amount ?? 0)
    const formatted = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    return `${code || currencyCode.value} ${formatted}`
  }

  return {
    currency,
    currencyCode,
    currencySymbol,
    formatAmount,
    formatPrice,
    formatAmountWithCode,
  }
}
